import { type ReactNode, useId, useLayoutEffect, useRef } from 'react';
import { cn, PaletteUtils } from '@shared/utils';
import { Text } from '@shared/widgets';

interface TextareaProps {
  className?: string;
  textareaClassName?: string;
  label?: ReactNode;
  fitContent?: boolean;
  rows?: number;
  placeholder?: string;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  minLength?: number;
  maxLength?: number;
  value?: string;
  onChange?: (value: string) => void;
}

export const Textarea = (props: TextareaProps) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const id = useId();

  useLayoutEffect(() => {
    if (!props.fitContent || !ref.current) return;

    ref.current.style.height = 'auto';
    ref.current.style.height = `${ref.current.scrollHeight}px`;
  }, [props.fitContent, props.value, ref.current]);

  return (
    <div className={cn(props.className)}>
      {props.label && (
        <label htmlFor={id} className='w-full'>
          <Text size='sm' color='secondary'>
            {props.label} {props.required ? ' *' : ''}
          </Text>
        </label>
      )}
      <textarea
        ref={ref}
        id={id}
        className={cn(
          'min-h-[84px] w-full resize-none overflow-hidden p-2 outline-none transition-all duration-200',
          PaletteUtils.isInsigniaPalette && [
            'border-b border-b-light bg-ice text-sm font-light focus:font-normal focus:text-soft-black dark:bg-soft-black',
            'hover:bg-white',
            'focus:border-b-primary focus:bg-white dark:focus:text-white',
          ],
          PaletteUtils.isVcfcPalette && [
            'rounded-2xl border border-light/70 bg-white px-5 dark:border-transparent dark:bg-white/10',
            'hover:border-primary dark:hover:border-light/70',
            'focus:border-primary dark:focus:border-light/70',
          ],
          props.readOnly && 'pointer-events-none opacity-30',
          props.disabled && 'pointer-events-none opacity-30',
          props.textareaClassName,
        )}
        rows={props.rows}
        placeholder={props.required ? `${props.placeholder} *` : props.placeholder}
        readOnly={props.readOnly}
        disabled={props.disabled}
        required={props.required}
        minLength={props.minLength}
        maxLength={props.maxLength}
        value={props.value}
        onChange={(e) => props.onChange?.(e.target.value)}
      />
    </div>
  );
};
